"use client";

import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type SearchMode = "posts" | "categories";

interface ContentSearchSidebarProps {
  categories: string[];
  selectedCategory: string;
  postSearch: string;
  contentLabel: string;
  postSearchPlaceholder: string;
  onPostSearchChange: (value: string) => void;
  onCategoryChange: (category: string) => void;
  defaultMode?: SearchMode;
}

export default function ContentSearchSidebar({
  categories,
  selectedCategory,
  postSearch,
  contentLabel,
  postSearchPlaceholder,
  onPostSearchChange,
  onCategoryChange,
  defaultMode = "posts",
}: ContentSearchSidebarProps) {
  const [mode, setMode] = useState<SearchMode>(defaultMode);
  const [categorySearch, setCategorySearch] = useState("");
  const [isXL, setIsXL] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsXL(window.innerWidth >= 1280);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const filteredCategoryList = useMemo(
    () =>
      categories.filter((category) =>
        category.toLowerCase().includes(categorySearch.toLowerCase())
      ),
    [categories, categorySearch]
  );

  const searchValue = mode === "posts" ? postSearch : categorySearch;
  const shouldShowCategoryList = isXL || mode === "categories";

  const handleSearchChange = (value: string) => {
    if (mode === "posts") {
      onPostSearchChange(value);
      return;
    }
    setCategorySearch(value);
  };

  return (
    <div className="bg-white rounded-lg p-4 xl:p-6 shadow-lg sticky top-4 xl:top-20">
      <h3 className="text-lg font-bold text-gray-800 mb-4">
        CATEGORIES
      </h3>

      <div className="mb-4 grid grid-cols-2 gap-1 rounded-md border border-gray-200 bg-gray-50 p-1">
        <button
          type="button"
          onClick={() => setMode("posts")}
          className={`rounded px-2 py-2 text-xs font-semibold transition-colors ${
            mode === "posts"
              ? "bg-white text-blue-700 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Search Posts
        </button>
        <button
          type="button"
          onClick={() => setMode("categories")}
          className={`rounded px-2 py-2 text-xs font-semibold transition-colors ${
            mode === "categories"
              ? "bg-white text-blue-700 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Search Categories
        </button>
      </div>

      <div className="relative mb-4 xl:mb-6">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 xl:h-5 xl:w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder={mode === "posts" ? postSearchPlaceholder : "Search categories..."}
          className="w-full pl-9 xl:pl-10 text-primary pr-4 py-2 xl:py-3 text-sm xl:text-base border border-gray-200 rounded-sm"
          value={searchValue}
          onChange={(event) => handleSearchChange(event.target.value)}
          aria-label={
            mode === "posts"
              ? `Search ${contentLabel}`
              : "Search categories"
          }
        />
      </div>

      {mode === "posts" && postSearch.trim() && (
        <button
          type="button"
          onClick={() => onPostSearchChange("")}
          className="mb-4 text-xs font-semibold text-blue-600 hover:text-blue-700"
        >
          Clear post search
        </button>
      )}

      {shouldShowCategoryList && (
        <div className="space-y-0 h-auto xl:h-[350px] overflow-y-auto custom-scrollbar transition-all duration-300">
          {filteredCategoryList.length > 0 ? (
            filteredCategoryList.map((category) => (
              <div key={category} className="border-b cursor-pointer border-gray-100 last:border-b-0">
                <button
                  onClick={() => onCategoryChange(category)}
                  className={`w-full cursor-pointer text-left py-2 xl:py-3 px-2 rounded transition-colors duration-200 text-sm xl:text-base ${
                    selectedCategory === category
                      ? "text-blue-600 bg-gray-50"
                      : "text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                  }`}
                >
                  {category}
                </button>
              </div>
            ))
          ) : (
            <p className="px-2 py-3 text-sm text-gray-500">
              No categories found.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
